import React, { useEffect, useState } from "react";
import { ReactComponent as PlusIconWhite } from "../../assets/icons/plus-white.svg";
import { ReactComponent as GroupIcon } from "../../assets/icons/group-icon.svg";
import { imageCache } from "../../utils/imageCache";
import AssetDashboardTabs from "./AssetDashboardTabs";

// This component represents the header section of the Asset Dashboard. It includes asset information,
// action buttons, and tabs for switching between different views.
// props for AssetDashboardHeader component
type AssetDashboardHeaderProps = {
  activeTab: "insights" | "map";
  onTabChange: (next: "insights" | "map") => void;
  assetGroup?: any;
  assetCount?: number;
  onAddAsset?: () => void;
  ownerOrOrgLabel?: string;
};
// AssetDashboardHeader component definition
const AssetDashboardHeader = ({
  activeTab,
  onTabChange,
  assetGroup = null,
  assetCount = 0,
  onAddAsset,
  ownerOrOrgLabel = "Unknown Owner",
}: AssetDashboardHeaderProps) => {
  const [assetGroupImage, setAssetGroupImage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadGroupImage = async () => {
      if (!assetGroup?.profilePictureUrl) {
        if (mounted) setAssetGroupImage(null);
        return;
      }

      try {
        const signedUrl = await imageCache.getSignedUrl(assetGroup.profilePictureUrl);
        if (mounted) {
          setAssetGroupImage(signedUrl || null);
        }
      } catch (error) {
        if (mounted) setAssetGroupImage(null);
      }
    };

    loadGroupImage();
    return () => {
      mounted = false;
    };
  }, [assetGroup?.profilePictureUrl]);

  const groupName = assetGroup?.name || "Asset Group";

  return (
    // Main container for the Asset Dashboard Header
    <div className="rounded-[8px] border border-eai-bg-lt2 bg-white px-3 pt-3 flex flex-col">
      {/* Asset Info and Action Buttons */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center pb-3">
        {assetGroupImage ? (
          <img
            src={assetGroupImage}
            alt={groupName}
            className="h-[56px] w-[86px] rounded-[5px] object-cover"
          />
        ) : (
          <div className="h-[56px] w-[86px] rounded-[5px] border border-eai-bg-lt2 bg-white flex items-center justify-center">
            <GroupIcon className="h-6 w-6 text-slate-500" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
            <h2 className="text-[18px] font-semibold text-black">{groupName}</h2>
            <div className="rounded-[8px] bg-[#F4F8E8] px-3 py-1 text-xs font-medium text-[#5B6B2B]">
              {assetCount} Assets
            </div>
            <div className="rounded-[8px] bg-[#DEF7EC] px-3 py-1 text-xs font-medium text-[#0E9F6E]">
              {ownerOrOrgLabel}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-[6px] bg-[#879643] px-3 py-2 text-xs font-medium text-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.1),0px_1px_2px_0px_rgba(16,24,40,0.06)]"
            onClick={onAddAsset}
          >
            New Asset
            <PlusIconWhite className="h-3 w-3" />
          </button>
        </div>
      </div>
      <div className="border-t border-eai-bg-lt2 -mx-3" />
      <div className="pt-3">
        <AssetDashboardTabs activeTab={activeTab} onTabChange={onTabChange} /> {/* Tabs for switching views (Insights and Asset Map) */}
      </div>
    </div>
  );
};

export default AssetDashboardHeader;
